import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { BellIcon } from '@heroicons/react/24/outline';
import { useUnreadServiceActivities } from '../../hooks/useUnreadServiceActivities';
import { useUnreadClientActivities } from '../../hooks/useUnreadClientActivities';
import { useUserNotifications } from '../../hooks/useUserNotifications';
import { useAuth } from '../../contexts/AuthContext';
import {
  BellCommand,
  isRepairRequestNotification,
  notificationService,
} from '../../services/notificationService';
import { RepairRequest } from '../../types/firestore';
import { AppNotification, AppNotificationType } from '../../types/notifications';
import {
  getRepairRequestOperationalIndexLabel,
  getRepairRequestSubjectLabel,
} from '../../shared/repairRequestDisplay';
import {
  canAccessServiceModule,
  canSubmitRepairRequest,
  isApprovedServiceAdmin,
} from '../../utils/accessControl';
import {
  buildRepairRequestReadModel,
  RepairRequestDetailSectionHint,
  RepairRequestUnreadSection,
} from '../../utils/repairRequestReadModel';
import { Modal } from '../common/UIComponents';
import { usePopoverFocusLifecycle } from '../../hooks/useOverlayFocusLifecycle';

type BellFeedItem = {
  key: string;
  source: 'notifications' | 'repairRequests';
  link?: string;
  title: string;
  subtitle: string | null;
  hint?: string | null;
  createdAtMs: number;
  displayDate: Date | null;
  canMarkRead: boolean;
  canDismiss: boolean;
  markReadCommand: BellCommand;
  dismissCommand?: BellCommand;
  notification?: AppNotification;
};

type PanelMode = 'normal' | 'manage';
type ActionState = 'idle' | 'marking' | 'dismissing';

const toDate = (value: { toDate?: () => Date } | undefined): Date | null => {
  if (!value || typeof value.toDate !== 'function') {
    return null;
  }
  try {
    const result = value.toDate();
    return result instanceof Date ? result : null;
  } catch (_error) {
    return null;
  }
};

const toMillis = (value: { toDate?: () => Date } | undefined): number => {
  const date = toDate(value);
  return date ? date.getTime() : 0;
};

const getReadableTypeCopy = (
  notification: AppNotification
): { title: string; subtitle: string } => {
  const fallbackTitle = notification.title || 'Nowe powiadomienie';
  const fallbackSubtitle = notification.body || 'Przejdź do szczegółów.';
  const typeCopy: Partial<Record<AppNotificationType, { title: string; subtitle: string }>> = {
    admin_new_verification: {
      title: fallbackTitle,
      subtitle: fallbackSubtitle,
    },
    service_verification_approved: {
      title: 'Profil serwisu zatwierdzony',
      subtitle: fallbackSubtitle,
    },
    service_verification_changes_requested: {
      title: 'Profil serwisu wymaga poprawek',
      subtitle: fallbackSubtitle,
    },
    admin_new_suggestion: {
      title: 'Nowa sugestia',
      subtitle: fallbackSubtitle,
    },
    suggestion_admin_reply: {
      title: 'Odpowiedź do sugestii',
      subtitle: fallbackSubtitle,
    },
    suggestion_accepted: {
      title: 'Sugestia zaakceptowana',
      subtitle: fallbackSubtitle,
    },
    suggestion_rejected: {
      title: 'Sugestia odrzucona',
      subtitle: fallbackSubtitle,
    },
    suggestion_voting_opened: {
      title: 'Głosowanie nad sugestią',
      subtitle: fallbackSubtitle,
    },
    suggestion_voting_reminder: {
      title: 'Przypomnienie o głosowaniu',
      subtitle: fallbackSubtitle,
    },
    suggestion_voting_closed_accepted: {
      title: 'Sugestia przeszła głosowanie',
      subtitle: fallbackSubtitle,
    },
    suggestion_voting_closed_rejected: {
      title: 'Sugestia nie przeszła głosowania',
      subtitle: fallbackSubtitle,
    },
    suggestion_declined_by_admin: {
      title: 'Sugestia zamknięta',
      subtitle: fallbackSubtitle,
    },
    suggestion_implementation_planned: {
      title: 'Sugestia trafiła do planu',
      subtitle: fallbackSubtitle,
    },
    suggestion_implementation_in_progress: {
      title: 'Sugestia jest wdrażana',
      subtitle: fallbackSubtitle,
    },
    suggestion_implementation_implemented: {
      title: 'Sugestia wdrożona',
      subtitle: fallbackSubtitle,
    },
    specialization_suggestion_accepted: {
      title: 'Specjalizacja zaakceptowana',
      subtitle: fallbackSubtitle,
    },
    specialization_suggestion_rejected: {
      title: 'Specjalizacja odrzucona',
      subtitle: fallbackSubtitle,
    },
    community_new_thread: {
      title: 'Nowy wątek w społeczności',
      subtitle: fallbackSubtitle,
    },
    community_reply: {
      title: 'Nowa odpowiedź w społeczności',
      subtitle: fallbackSubtitle,
    },
    community_thread_reply: {
      title: 'Nowa odpowiedź w społeczności',
      subtitle: fallbackSubtitle,
    },
    community_quote: {
      title: 'Ktoś zacytował Twój wpis',
      subtitle: fallbackSubtitle,
    },
    community_gratitude_targeted: {
      title: 'Nowe podziękowanie',
      subtitle: fallbackSubtitle,
    },
    community_gratitude_reply: {
      title: 'Odpowiedź na podziękowanie',
      subtitle: fallbackSubtitle,
    },
    community_announcement: {
      title: 'Ogłoszenie społeczności',
      subtitle: fallbackSubtitle,
    },
    community_moderation_action: {
      title: 'Aktualizacja moderacji',
      subtitle: fallbackSubtitle,
    },
    admin_new_community_report: {
      title: 'Nowe zgłoszenie treści na forum',
      subtitle: fallbackSubtitle,
    },
  };

  return typeCopy[notification.type] || { title: fallbackTitle, subtitle: fallbackSubtitle };
};

const getServicePrimaryLabel = ({
  section,
  hasFreshRequestActivity,
}: {
  section: string | null;
  hasFreshRequestActivity: boolean;
}) => {
  if (hasFreshRequestActivity) {
    return 'Nowe zgłoszenie';
  }
  switch (section) {
    case 'quote':
      return 'Decyzja o wycenie';
    case 'messages':
      return 'Nowa wiadomość od klienta';
    case 'status':
      return 'Aktualizacja statusu zgłoszenia';
    default:
      return 'Nowa aktywność zgłoszenia';
  }
};

const getServiceSecondaryHint = ({
  section,
  hasFreshRequestActivity,
}: {
  section: string | null;
  hasFreshRequestActivity: boolean;
}) => {
  if (hasFreshRequestActivity) {
    return 'Sprawdź opis problemu i zdecyduj o pierwszym kroku.';
  }
  switch (section) {
    case 'messages':
      return 'Czeka wiadomość w zgłoszeniu.';
    case 'quote':
      return 'Sprawdź decyzję właściciela zgłoszenia.';
    case 'status':
      return 'Status sprawy został zaktualizowany.';
    default:
      return null;
  }
};

const getClientPrimaryLabel = (section: string | null) => {
  switch (section) {
    case 'quote':
      return 'Nowa wycena';
    case 'messages':
      return 'Nowa wiadomość od serwisu';
    case 'status':
      return 'Aktualizacja statusu zgłoszenia';
    default:
      return 'Nowa aktywność zgłoszenia';
  }
};

const getClientSecondaryHint = (section: string | null) => {
  switch (section) {
    case 'messages':
      return 'Czeka wiadomość w zgłoszeniu.';
    case 'quote':
      return 'Sprawdź wycenę od serwisu.';
    case 'status':
      return 'Status sprawy został zaktualizowany.';
    default:
      return null;
  }
};

const buildRequestLink = (
  requestId: string,
  section: RepairRequestDetailSectionHint | null,
  actor: 'client' | 'service'
) => {
  const returnParams = new URLSearchParams();
  returnParams.set('request', requestId);
  const returnTo =
    actor === 'service'
      ? `/app/service?${returnParams.toString()}`
      : `/app/my-requests?${returnParams.toString()}`;
  const detailParams = new URLSearchParams();
  detailParams.set('returnTo', returnTo);
  switch (section) {
    case 'messages':
    case 'description':
    case 'settlement':
    case 'review':
      detailParams.set('section', section);
      break;
    default:
      break;
  }
  return `/app/request/${requestId}?${detailParams.toString()}`;
};

const getDeviceName = (req: RepairRequest) => {
  const parts = [getRepairRequestSubjectLabel(req), req.brand, req.model].filter(Boolean);
  return parts.length > 0 ? parts.join(' ') : 'Nieznane urządzenie';
};

const getActionErrorMessage = (failedCount: number, totalCount: number) => {
  if (failedCount === totalCount) {
    return 'Nie udało się wykonać akcji. Sprawdź połączenie albo uprawnienia i spróbuj ponownie.';
  }
  return `Część powiadomień została obsłużona, ale ${failedCount} nie udało się zaktualizować.`;
};

export const NotificationBell: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, userProfile, claims } = useAuth();
  const serviceState = useUnreadServiceActivities();
  const clientState = useUnreadClientActivities();
  const { notifications: unreadNotifications, loading: notificationsLoading } =
    useUserNotifications({
      onlyUnread: true,
      maxResults: 40,
    });

  const showServiceLegacy = isApprovedServiceAdmin(userProfile, claims);
  const showClientLegacy = canSubmitRepairRequest(userProfile, claims);
  const canAccessServiceProfile = canAccessServiceModule(userProfile, claims);
  const defaultTarget = location.pathname.startsWith('/admin') ? '/admin' : '/app';

  const [isOpen, setIsOpen] = useState(false);
  const [mode, setMode] = useState<PanelMode>('normal');
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const [confirmDismissOpen, setConfirmDismissOpen] = useState(false);
  const [actionState, setActionState] = useState<ActionState>('idle');
  const [panelMessage, setPanelMessage] = useState<string | null>(null);
  const [focusPopoverOnOpen, setFocusPopoverOnOpen] = useState(false);
  const [isMobileViewport, setIsMobileViewport] = useState(() =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia('(max-width: 639px)').matches
      : false
  );
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const desktopPanelRef = useRef<HTMLDivElement>(null);
  const [portalTarget, setPortalTarget] = useState<HTMLElement | null>(null);

  const { closePopover } = usePopoverFocusLifecycle({
    isOpen: isOpen && !isMobileViewport,
    panelRef: desktopPanelRef,
    triggerRef,
    onClose: () => setIsOpen(false),
    focusOnOpen: focusPopoverOnOpen,
  });

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!isOpen || isMobileViewport) return;
      const target = event.target as Node;
      const clickedInsideContainer = containerRef.current?.contains(target);
      if (!clickedInsideContainer) {
        closePopover();
        setConfirmDismissOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [closePopover, isMobileViewport, isOpen]);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;

    const mediaQuery = window.matchMedia('(max-width: 639px)');
    const handleViewportChange = (event: MediaQueryListEvent) => {
      setIsMobileViewport(event.matches);
    };

    setIsMobileViewport(mediaQuery.matches);
    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', handleViewportChange);
      return () => mediaQuery.removeEventListener('change', handleViewportChange);
    }

    mediaQuery.addListener(handleViewportChange);
    return () => mediaQuery.removeListener(handleViewportChange);
  }, []);

  useEffect(() => {
    setPortalTarget(document.body);
  }, []);

  useEffect(() => {
    if (!isOpen) {
      setFocusPopoverOnOpen(false);
      setMode('normal');
      setSelectedKeys(new Set());
      setPanelMessage(null);
      setConfirmDismissOpen(false);
    }
  }, [isOpen]);

  const serviceLegacyItems: BellFeedItem[] = showServiceLegacy
    ? serviceState.unreadRequests.map((req) => {
        const readModel = buildRepairRequestReadModel(req);
        const requestId = readModel.requestId || 'unknown';
        const primarySection = readModel.unread.service.primarySection || 'messages';
        const secondarySection = readModel.unread.service.secondarySection;
        const hasFreshRequestActivity = readModel.unread.service.hasFreshRequestActivity;

        return {
          source: 'repairRequests',
          key: `legacy:service:${requestId}`,
          link: buildRequestLink(requestId, readModel.unread.service.detailSection, 'service'),
          title: getServicePrimaryLabel({ section: primarySection, hasFreshRequestActivity }),
          subtitle: getServiceSecondaryHint({
            section: secondarySection || primarySection,
            hasFreshRequestActivity,
          }),
          hint: `${getRepairRequestOperationalIndexLabel(req)} • ${getDeviceName(req)}`,
          createdAtMs: toMillis(req.lastServiceActivityAt),
          displayDate: toDate(req.lastServiceActivityAt),
          canMarkRead: true,
          canDismiss: false,
          markReadCommand: {
            source: 'repairRequests',
            action: 'markRead',
            requestId,
            unreadSection: primarySection as RepairRequestUnreadSection,
            actor: 'service',
          },
        };
      })
    : [];

  const clientLegacyItems: BellFeedItem[] = showClientLegacy
    ? clientState.unreadRequests.map((req) => {
        const readModel = buildRepairRequestReadModel(req);
        const requestId = readModel.requestId || 'unknown';
        const primarySection = readModel.unread.client.primarySection || 'messages';
        const secondarySection = readModel.unread.client.secondarySection;

        return {
          source: 'repairRequests',
          key: `legacy:client:${requestId}`,
          link: buildRequestLink(requestId, readModel.unread.client.detailSection, 'client'),
          title: getClientPrimaryLabel(primarySection),
          subtitle: getClientSecondaryHint(secondarySection || primarySection),
          hint: `${getRepairRequestOperationalIndexLabel(req)} • ${getDeviceName(req)}`,
          createdAtMs: toMillis(req.lastClientActivityAt),
          displayDate: toDate(req.lastClientActivityAt),
          canMarkRead: true,
          canDismiss: false,
          markReadCommand: {
            source: 'repairRequests',
            action: 'markRead',
            requestId,
            unreadSection: primarySection as RepairRequestUnreadSection,
            actor: 'client',
          },
        };
      })
    : [];

  const eventItems: BellFeedItem[] = unreadNotifications
    .filter((notification) => !isRepairRequestNotification(notification))
    .filter((notification) => !notification.dismissedAt)
    .map((notification) => {
      const copy = getReadableTypeCopy(notification);
      return {
        source: 'notifications',
        key: `event:${notification.id}`,
        title: copy.title,
        subtitle: copy.subtitle,
        createdAtMs: notification.createdAt?.toMillis?.() || 0,
        displayDate: toDate(notification.createdAt),
        notification,
        canMarkRead: true,
        canDismiss: true,
        markReadCommand: {
          source: 'notifications',
          action: 'markRead',
          id: notification.id,
          entityType: notification.entityType,
          entityId: notification.entityId,
        },
        dismissCommand: {
          source: 'notifications',
          action: 'dismiss',
          id: notification.id,
          entityType: notification.entityType,
          entityId: notification.entityId,
        },
      };
    });

  const items: BellFeedItem[] = [...serviceLegacyItems, ...clientLegacyItems, ...eventItems].sort(
    (a, b) => {
      if (b.createdAtMs !== a.createdAtMs) {
        return b.createdAtMs - a.createdAtMs;
      }
      return a.key.localeCompare(b.key);
    }
  );

  const loading = notificationsLoading || serviceState.loading || clientState.loading;
  const badgeCount = items.length;
  const selectedItems = items.filter((item) => selectedKeys.has(item.key));
  const selectedDismissableCount = selectedItems.filter((item) => item.canDismiss).length;
  const selectedRequestCount = selectedItems.filter(
    (item) => item.source === 'repairRequests'
  ).length;
  const selectedCount = selectedItems.length;

  const toggleSelection = (itemKey: string) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(itemKey)) {
        next.delete(itemKey);
      } else {
        next.add(itemKey);
      }
      return next;
    });
  };

  const enterManageMode = () => {
    setPanelMessage(null);
    setMode('manage');
  };

  const cancelManageMode = () => {
    setMode('normal');
    setSelectedKeys(new Set());
    setPanelMessage(null);
    setConfirmDismissOpen(false);
  };

  const runCommands = async (commands: BellCommand[], nextActionState: ActionState) => {
    if (!currentUser?.uid || commands.length === 0) {
      return;
    }
    setActionState(nextActionState);
    setPanelMessage(null);
    const results = await notificationService.applyBellCommands(commands, currentUser.uid);
    const failed = results.filter((result) => !result.ok);
    if (failed.length > 0) {
      setPanelMessage(getActionErrorMessage(failed.length, results.length));
    } else {
      setPanelMessage(null);
      setSelectedKeys(new Set());
      setMode('normal');
    }
    setActionState('idle');
  };

  const markSelectedAsRead = async () => {
    await runCommands(
      selectedItems.filter((item) => item.canMarkRead).map((item) => item.markReadCommand),
      'marking'
    );
  };

  const dismissSelectedEvents = async () => {
    const commands = selectedItems
      .filter((item) => item.canDismiss && item.dismissCommand)
      .map((item) => item.dismissCommand as BellCommand);
    if (commands.length === 0 && selectedRequestCount > 0) {
      await markSelectedAsRead();
      setConfirmDismissOpen(false);
      return;
    }
    await runCommands(commands, 'dismissing');
    setConfirmDismissOpen(false);
  };

  const handleEventClick = async (notification: AppNotification): Promise<void> => {
    setPanelMessage(null);

    const { target } = notificationService.resolveNotificationNavigationTarget(notification, {
      canAccessServiceProfile,
      defaultTarget,
    });
    const hasSpecificTarget = Boolean(notification.deepLink || notification.entityId);
    if (!hasSpecificTarget || target === defaultTarget) {
      setPanelMessage(
        'Nie możemy otworzyć tego powiadomienia. Element mógł zostać usunięty albo nie masz już do niego dostępu.'
      );
      return;
    }

    setIsOpen(false);
    navigate(target);
  };

  const renderItem = (item: BellFeedItem) => {
    const dateLabel = item.displayDate ? item.displayDate.toLocaleDateString('pl-PL') : null;
    const selected = selectedKeys.has(item.key);
    const cardClass = [
      'block w-full px-4 py-3 text-left bg-primary-dark-100/50 rounded-lg transition-colors border-l-2 group',
      selected ? 'border-ratuj-300 ring-1 ring-ratuj-300/40' : 'border-ratuj-300',
      mode === 'manage'
        ? 'cursor-pointer hover:bg-primary-dark-300/40'
        : 'hover:bg-primary-dark-300/50',
    ].join(' ');
    const content = (
      <>
        <div className="flex justify-between items-start mb-1.5 gap-2">
          <span className="text-sm font-medium text-primary-light-100 group-hover:text-ratuj-300 transition-colors leading-tight break-words line-clamp-2">
            {item.title}
          </span>
          {dateLabel && (
            <span className="text-[10px] text-primary-light-400 whitespace-nowrap shrink-0 mt-0.5">
              {dateLabel}
            </span>
          )}
        </div>
        {item.subtitle && (
          <p className="text-xs text-primary-light-300 leading-snug break-words line-clamp-2">
            {item.subtitle}
          </p>
        )}
        {item.hint && (
          <p className="mt-1 text-xs text-primary-light-300 leading-snug break-words line-clamp-1">
            {item.hint}
          </p>
        )}
      </>
    );

    if (mode === 'manage') {
      return (
        <button
          key={item.key}
          type="button"
          onClick={() => toggleSelection(item.key)}
          className={`${cardClass} flex gap-3 items-start`}
          aria-pressed={selected}
        >
          <span className="mt-0.5 flex h-11 w-11 shrink-0 items-center justify-center">
            <span
              className={`h-5 w-5 rounded-md border ${
                selected
                  ? 'border-ratuj-300 bg-ratuj-300 shadow-highlight'
                  : 'border-primary-dark-400 bg-primary-dark-200'
              }`}
            />
          </span>
          <span className="min-w-0 flex-1">{content}</span>
        </button>
      );
    }

    if (item.source === 'notifications' && item.notification) {
      return (
        <button
          key={item.key}
          type="button"
          onClick={() => {
            void handleEventClick(item.notification as AppNotification);
          }}
          className={cardClass}
        >
          {content}
        </button>
      );
    }

    return (
      <Link
        key={item.key}
        to={item.link || '/app'}
        onClick={() => setIsOpen(false)}
        className={cardClass}
      >
        {content}
      </Link>
    );
  };

  const renderMessage = () =>
    panelMessage ? (
      <div className="mx-2 mt-2 rounded-lg border border-status-warning-500/30 bg-status-warning-500/10 px-3 py-2 text-xs leading-snug text-status-warning-100">
        {panelMessage}
      </div>
    ) : null;

  const renderHeader = () => (
    <div className="px-4 py-3 border-b border-primary-dark-300/70 bg-primary-dark-300/20 font-semibold text-primary-light-100 flex justify-between items-center shrink-0">
      <div className="min-w-0">
        <span className="block text-sm sm:text-sm">
          {mode === 'manage' ? `${selectedCount} zaznaczone` : 'Powiadomienia'}
        </span>
        {mode === 'manage' && selectedRequestCount > 0 && (
          <span className="block text-[11px] font-normal text-primary-light-400">
            Zgłoszenia można tylko oznaczyć jako przeczytane.
          </span>
        )}
      </div>
      <div className="flex shrink-0 items-center gap-2">
        {mode === 'manage' ? (
          <button
            type="button"
            onClick={cancelManageMode}
            className="min-h-[34px] rounded-lg px-3 text-xs font-medium text-primary-light-200 hover:bg-primary-dark-300"
          >
            Anuluj
          </button>
        ) : (
          <>
            {badgeCount > 0 && (
              <span className="text-xs bg-ratuj-300/20 px-2 py-1 rounded-md text-ratuj-300 font-medium">
                {badgeCount} nowe
              </span>
            )}
            {items.length > 0 && (
              <button
                type="button"
                onClick={enterManageMode}
                className="min-h-[34px] rounded-lg px-3 text-xs font-medium text-primary-light-200 hover:bg-primary-dark-300"
              >
                Zarządzaj
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );

  const renderList = () => (
    <div className="overflow-y-auto flex-1 min-h-0 p-2 scrollbar-thin scrollbar-thumb-primary-dark-300 scrollbar-track-transparent">
      {loading ? (
        <div className="px-4 py-8 text-center text-primary-light-400 text-sm flex flex-col items-center justify-center gap-2">
          <BellIcon className="w-8 h-8 opacity-40 mb-1 animate-pulse" />
          Ładujemy powiadomienia
        </div>
      ) : items.length === 0 ? (
        <div className="px-4 py-8 text-center text-primary-light-400 text-sm flex flex-col items-center justify-center gap-2">
          <BellIcon className="w-8 h-8 opacity-40 mb-1" />
          Brak nowych powiadomień
        </div>
      ) : (
        <div
          className={`flex flex-col gap-1.5 ${
            mode === 'manage' ? 'pb-24 sm:pb-20' : 'pb-[env(safe-area-inset-bottom)]'
          }`}
        >
          {items.map((item) => renderItem(item))}
        </div>
      )}
    </div>
  );

  const renderActionBar = () =>
    mode === 'manage' && items.length > 0 ? (
      <div className="sticky bottom-0 z-10 border-t border-primary-dark-300 bg-primary-dark-200/95 p-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] backdrop-blur">
        <div className="flex flex-col gap-2 sm:flex-row">
          <button
            type="button"
            disabled={selectedCount === 0 || actionState !== 'idle'}
            onClick={() => {
              void markSelectedAsRead();
            }}
            className="min-h-[44px] flex-1 rounded-lg bg-ratuj-400 px-3 text-sm font-semibold text-primary-dark-100 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {actionState === 'marking' ? 'Oznaczanie...' : 'Oznacz jako przeczytane'}
          </button>
          <button
            type="button"
            disabled={selectedCount === 0 || actionState !== 'idle'}
            onClick={() => setConfirmDismissOpen(true)}
            className="min-h-[44px] flex-1 rounded-lg border border-status-error-500/50 px-3 text-sm font-semibold text-status-error-100 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {selectedDismissableCount === 0 ? 'Oznacz jako przeczytane' : 'Usuń z listy'}
          </button>
        </div>
      </div>
    ) : null;

  const renderConfirm = () =>
    confirmDismissOpen ? (
      <div className="absolute inset-0 z-20 flex items-end justify-center bg-black/55 p-3 sm:items-center">
        <div className="w-full max-w-[calc(100vw-32px)] rounded-lg border border-primary-dark-300 bg-primary-dark-200 p-4 shadow-dropdown sm:max-w-sm">
          <h3 className="text-base font-semibold text-primary-light-100">
            {selectedDismissableCount === 0 ? 'Oznaczyć jako przeczytane?' : 'Usunąć z listy?'}
          </h3>
          <p className="mt-2 text-sm leading-5 text-primary-light-300">
            Ukryjemy wybrane powiadomienia z listy. Nie usunie to zgłoszeń, wątków ani danych
            biznesowych.
          </p>
          {selectedRequestCount > 0 && (
            <p className="mt-2 text-xs leading-5 text-primary-light-400">
              Powiadomienia ze zgłoszeń można tylko oznaczyć jako przeczytane, bo ich źródłem jest
              aktywność zgłoszenia.
            </p>
          )}
          <div className="mt-4 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={() => setConfirmDismissOpen(false)}
              className="min-h-[42px] rounded-lg bg-primary-dark-300 px-4 text-sm font-medium text-primary-light-100"
            >
              Anuluj
            </button>
            <button
              type="button"
              onClick={() => {
                void dismissSelectedEvents();
              }}
              className="min-h-[42px] rounded-lg bg-status-error-500 px-4 text-sm font-semibold text-white disabled:opacity-60"
              disabled={actionState !== 'idle'}
            >
              {selectedDismissableCount === 0 ? 'Oznacz jako przeczytane' : 'Usuń z listy'}
            </button>
          </div>
        </div>
      </div>
    ) : null;

  const renderPanel = (mobile: boolean) => (
    <div
      ref={mobile ? undefined : desktopPanelRef}
      role={mobile ? undefined : 'dialog'}
      aria-label={mobile ? undefined : 'Powiadomienia'}
      tabIndex={mobile ? undefined : -1}
      className={
        mobile
          ? 'relative flex h-full min-h-0 flex-col overflow-hidden leading-normal'
          : 'absolute top-full right-0 mt-3 w-[24rem] rounded-xl shadow-dropdown bg-primary-dark-200/95 backdrop-blur-md border border-primary-dark-300/70 z-[60] flex flex-col max-h-[85vh] animate-fade-in origin-top-right overflow-hidden'
      }
    >
      {renderHeader()}
      {renderMessage()}
      {renderList()}
      {renderActionBar()}
      {mobile && mode === 'normal' && (
        <div className="p-4 border-t border-primary-dark-300 pb-[calc(1rem+env(safe-area-inset-bottom))] bg-primary-dark-200 shrink-0">
          <button
            type="button"
            onClick={() => setIsOpen(false)}
            className="w-full py-3 rounded-lg bg-primary-dark-300 text-primary-light-100 font-medium active:bg-primary-dark-100"
          >
            Zamknij
          </button>
        </div>
      )}
      {renderConfirm()}
    </div>
  );

  return (
    <div className="relative flex items-center" ref={containerRef}>
      <button
        ref={triggerRef}
        type="button"
        aria-label="Powiadomienia"
        aria-expanded={isOpen}
        aria-haspopup="dialog"
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown' && !isOpen) {
            event.preventDefault();
            setFocusPopoverOnOpen(true);
            setIsOpen(true);
          }
        }}
        onClick={(event) => {
          if (isOpen) {
            if (isMobileViewport) {
              setIsOpen(false);
            } else {
              closePopover({ returnFocus: event.detail === 0 });
            }
            return;
          }

          setFocusPopoverOnOpen(event.detail === 0);
          setIsOpen(true);
        }}
        className="relative p-2 text-primary-light-300 hover:text-ratuj-300 transition-colors rounded-full focus:outline-none focus:ring-2 focus:ring-ratuj-300 flex-shrink-0"
        title="Powiadomienia"
      >
        <BellIcon className="w-5 h-5 md:w-6 md:h-6" />
        {badgeCount > 0 && (
          <span className="absolute top-1 right-1 flex items-center justify-center w-4 h-4 text-[10px] font-bold text-primary-dark-100 bg-status-error-500 rounded-full border border-primary-dark-200">
            {badgeCount > 9 ? '9+' : badgeCount}
          </span>
        )}
      </button>

      {isOpen && (
        <>
          {isMobileViewport && portalTarget
            ? createPortal(
                <Modal
                  isOpen
                  onClose={() => setIsOpen(false)}
                  ariaLabel="Powiadomienia"
                  returnFocusRef={triggerRef}
                  placement="bottom-responsive"
                  overlayClassName="!z-[60] !bg-black/60 backdrop-blur-sm sm:hidden"
                  overlayTestId="notification-mobile-overlay"
                  dialogTestId="notification-mobile-dialog"
                  className="relative flex !h-[85dvh] !min-h-[16rem] !max-h-[85dvh] !w-full !max-w-none flex-col !overflow-hidden !rounded-b-none !rounded-t-2xl !border-primary-dark-300/70"
                >
                  {renderPanel(true)}
                </Modal>,
                portalTarget
              )
            : null}

          {!isMobileViewport ? renderPanel(false) : null}
        </>
      )}
    </div>
  );
};
